// ─── Rattachements estimé ↔ réel : accès Firestore ────────────────
// Les CALCULS vivent dans `repartition.js` (module pur, testé). Ici on ne fait
// que lire et écrire — la séparation est ce qui rend la règle d'argent
// vérifiable par `npm test`.
//
//   rattachements/{id} { espaceId, depenseId, ligneId, mois, montant,
//                        origine: 'manuel'|'propose', creePar, creeLe }
//
// ⚠️ Rien n'est écrit sans l'accord de l'utilisateur (règle générale de l'app).
// `origine: 'propose'` marque ce que l'app avait suggéré et que l'utilisateur a
// CONFIRMÉ — jamais une écriture d'office.
import { db } from '@mop/core';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot, query, where,
  getDocs, writeBatch,
} from 'firebase/firestore';
import { verifierRattachement, calculerPart } from './repartition.js';

export function ecouterRattachements(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  const q = query(collection(db, 'rattachements'), where('espaceId', '==', espaceId));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (e) => { callback([]); onErreur?.(e); });
}

const corps = ({ espaceId, depenseId, ligneId, mois, montant, origine, creePar }) => ({
  espaceId, depenseId, ligneId, mois,
  montant: Math.round(Math.abs(Number(montant) || 0) * 100) / 100,
  origine: origine === 'propose' ? 'propose' : 'manuel',
  creePar: creePar || null,
  creeLe: new Date().toISOString(),
});

// Le contrôle de cohérence est fait ICI, avant l'écriture, et son motif
// remonte tel quel : une écriture refusée en silence laisserait l'utilisateur
// devant un écran qui ne bouge pas, sans savoir pourquoi.
export async function rattacher({ depense, rattachements = [], ...champs }) {
  const v = verifierRattachement({ depense, rattachements, ...champs });
  if (!v.ok) throw new Error(`rattachement.${v.motif}`);
  const ref = await addDoc(collection(db, 'rattachements'),
    corps({ ...champs, depenseId: depense.id }));
  return ref.id;
}

export async function modifierRattachement(id, { depense, rattachements = [], ...champs }) {
  const v = verifierRattachement({ depense, rattachements, ignorerId: id, ...champs });
  if (!v.ok) throw new Error(`rattachement.${v.motif}`);
  await updateDoc(doc(db, 'rattachements', id), {
    montant: Math.round(Math.abs(Number(champs.montant) || 0) * 100) / 100,
    ligneId: champs.ligneId, mois: champs.mois,
  });
}

export const detacher = (id) => deleteDoc(doc(db, 'rattachements', id));

// Supprimer une opération réelle ou une ligne prévue laisse sinon des
// rattachements orphelins, qui gonfleraient le réel d'une occurrence sans
// qu'aucun écran ne puisse les montrer.
export async function detacherTousDe({ espaceId, depenseId = null, ligneId = null }) {
  if (!espaceId || (!depenseId && !ligneId)) return 0;
  const snap = await getDocs(query(collection(db, 'rattachements'), where('espaceId', '==', espaceId)));
  const refs = snap.docs
    .filter(d => (depenseId ? d.data().depenseId === depenseId : d.data().ligneId === ligneId))
    .map(d => d.ref);
  for (let i = 0; i < refs.length; i += 450) {
    const batch = writeBatch(db);
    refs.slice(i, i + 450).forEach(r => batch.delete(r));
    await batch.commit();
  }
  return refs.length;
}

// ── Appliquer un diff de sélection (spéc. du 12/08, §11) ─────────
// La spec parle d'un serveur qui reçoit un diff et recalcule ; il n'y en a
// pas : on écrit dans Firestore. Même contrat, autre plomberie — et un LOT
// ATOMIQUE, sans quoi une coupure laisserait une opération à 150 % répartis.
//
// Renvoie { ajoutes, retires, lignesRecalculees } pour alimenter le toast :
// « 3 opérations rattachées · 2 autres prévisions recalculées ».
export async function appliquerDiffRattachements({
  espaceId, ligneId, operations = [], rattachements = [],
  ajouts = [], retraits = [], moisParOperation = {}, creePar,
}) {
  if (!espaceId || !ligneId) throw new Error('rattachement.cible');
  const parId = new Map(operations.map(o => [o?.id, o]));
  const batch = writeBatch(db);
  // Copie de travail : chaque ajout doit voir l'effet des précédents, sinon
  // deux opérations traitées dans le même lot calculeraient leur part sur un
  // état périmé et pourraient dépasser 100 % ensemble.
  let etat = [...rattachements];
  const lignesTouchees = new Set();
  let ajoutes = 0;

  for (const depenseId of ajouts) {
    const op = parId.get(depenseId);
    if (!op) continue;
    const p = calculerPart(op, etat, ligneId);
    if (!p.possible) continue;                 // §15 : part sous le centime

    // Les ajustements réduisent les parts des AUTRES prévisions (§10.2).
    for (const a of p.ajustements) {
      batch.update(doc(db, 'rattachements', a.id), { montant: a.montant });
      etat = etat.map(r => (r.id === a.id ? { ...r, montant: a.montant } : r));
      lignesTouchees.add(a.ligneId);
    }

    const mois = moisParOperation[depenseId] || String(op.dateBanque || op.datePaiement || op.date || '').slice(0, 7);
    const ref = doc(collection(db, 'rattachements'));
    const nouveau = {
      espaceId, depenseId, ligneId, mois,
      montant: p.montant, origine: 'manuel',
      creePar: creePar || null, creeLe: new Date().toISOString(),
    };
    batch.set(ref, nouveau);
    etat = [...etat, { id: ref.id, ...nouveau }];
    ajoutes++;
  }

  // Retraits : la part est libérée SANS redistribution (§10.5). Le prochain
  // budget qui sélectionnera l'opération la récupérera via `reste`.
  let retires = 0;
  for (const depenseId of retraits) {
    for (const r of rattachements.filter(x => x?.depenseId === depenseId && x.ligneId === ligneId)) {
      batch.delete(doc(db, 'rattachements', r.id));
      retires++;
    }
  }

  await batch.commit();
  return { ajoutes, retires, lignesRecalculees: lignesTouchees.size };
}
